import React, { useState } from 'react';
import NavigationBar from '@/components/ui/NavigationBar';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { hasFeatureAccess } from '@/config/featureAccess';
import { NovaChatProvider } from '@/contexts/NovaChatContext';
import NovaChat from '@/components/NovaChat';
import UGCLayout from '@/components/layouts/UGCLayout';

const UGCCommunityPage = () => {
  const { profile } = useAuth();
  const { ugcTier } = useSubscription();
  const userTier = ugcTier || profile?.ugc_tier || 'free';
  const canAccess = hasFeatureAccess('app/ugc-dashboard', userTier);
  const [activeTab, setActiveTab] = useState('feedback');

  const ugcNavItems = [
    { path: '/app/ugc-hub', label: 'Creator Hub', icon: 'Compass' },
    { path: '/app/ugc-brands', label: 'Brands', icon: 'Database' },
    { path: '/app/ugc-crm', label: 'CRM', icon: 'Users' },
    { path: '/app/ugc-community', label: 'Community', icon: 'MessageCircle' },
    { path: '/app/ugc-scripts', label: 'Scripts', icon: 'FileText' },
    { path: '/app/teleprompter', label: 'Teleprompter', icon: 'PlayCircle' },
  ];

  const feedbackPrompts = [
    {
      category: 'Hook Analysis',
      icon: 'Target',
      color: 'purple',
      prompts: [
        'Does my hook grab attention in the first second?',
        'Is the value proposition clear immediately?',
        'Would this hook stop someone mid-scroll?',
        'How can I make this hook more compelling?'
      ]
    },
    {
      category: 'CTA Optimization',
      icon: 'MousePointer',
      color: 'blue',
      prompts: [
        'Is my call-to-action clear and actionable?',
        'Does the CTA match the content promise?',
        'Should I add urgency to my CTA?',
        'What CTA would you use instead?'
      ]
    },
    {
      category: 'Engagement Tips',
      icon: 'ThumbsUp',
      color: 'green',
      prompts: [
        'What would make you comment on this?',
        'Would you share this with a friend?',
        'Is there a better time to post this?',
        'How can I boost watch time?'
      ]
    },
    {
      category: 'Visual Quality',
      icon: 'Eye',
      color: 'orange',
      prompts: [
        'Is the lighting/framing professional?',
        'Are text overlays readable?',
        'Does the visual flow make sense?',
        'What visual changes would improve this?'
      ]
    }
  ];

  const collaborationOpportunities = [
    {
      type: 'Content Swap',
      description: 'Exchange UGC videos with creators in complementary niches',
      participants: '2-3 creators',
      benefit: 'Cross-promote to new audiences',
      icon: 'Repeat',
      color: 'pink'
    },
    {
      type: 'Script Buddy',
      description: 'Pair up to review and improve each other\'s scripts',
      participants: '2 creators',
      benefit: 'Get fresh perspective on your copy',
      icon: 'Edit',
      color: 'blue'
    },
    {
      type: 'Brand Bundle',
      description: 'Team up to pitch brands as a creator package',
      participants: '3-5 creators',
      benefit: 'Higher rates and bigger campaigns',
      icon: 'Users',
      color: 'purple'
    },
    {
      type: 'Accountability Pod',
      description: 'Weekly check-ins with goals and progress tracking',
      participants: '4-6 creators',
      benefit: 'Stay consistent and motivated',
      icon: 'Calendar',
      color: 'green'
    }
  ];

  const communityResources = [
    {
      title: 'Creator Showcase',
      description: 'Share your best work and get discovered',
      icon: 'Award',
      coming: false,
      action: 'Share Content'
    },
    {
      title: 'Feedback Forum',
      description: 'Post videos for peer review and Lumo AI analysis',
      icon: 'MessageSquare',
      coming: false,
      action: 'Get Feedback'
    },
    {
      title: 'Success Stories',
      description: 'Learn from creators who landed major brand deals',
      icon: 'TrendingUp',
      coming: false,
      action: 'Read Stories'
    },
    {
      title: 'Expert AMAs',
      description: 'Monthly Q&A sessions with top UGC creators',
      icon: 'Video',
      coming: true,
      action: 'Coming Soon'
    }
  ];

  const sampleFeedback = [
    {
      creator: 'Sarah M.',
      content: 'Posted hook: "Stop scrolling if you hate dark circles..."',
      responses: 12,
      likes: 24,
      category: 'Hook Analysis',
      timeAgo: '2h ago',
      preview: 'Love this! But maybe make it more specific: "If you have dark circles that makeup can\'t hide..." - creates more urgency'
    },
    {
      creator: 'Mike T.',
      content: 'Need CTA feedback for my skincare video',
      responses: 8,
      likes: 15,
      category: 'CTA Optimization',
      timeAgo: '5h ago',
      preview: 'Try "Link in bio for 20% off - TODAY ONLY" instead of just "check bio". Scarcity works!'
    },
    {
      creator: 'Emma L.',
      content: 'Script review: 15s protein shake routine',
      responses: 6,
      likes: 11,
      category: 'Script Review',
      timeAgo: '1d ago',
      preview: 'Your pacing is perfect but the transition at 8s feels abrupt. Maybe add a quick "here\'s why" before the next shot?'
    }
  ];

  const getBgColorClass = (color) => {
    const colors = {
      pink: 'bg-pink-500/10 border-pink-500/20',
      purple: 'bg-purple-500/10 border-purple-500/20',
      blue: 'bg-blue-500/10 border-blue-500/20',
      green: 'bg-green-500/10 border-green-500/20',
      orange: 'bg-orange-500/10 border-orange-500/20'
    };
    return colors[color] || colors.blue;
  };

  const getIconColorClass = (color) => {
    const colors = {
      pink: 'text-pink-600',
      purple: 'text-purple-600',
      blue: 'text-blue-600',
      green: 'text-green-600',
      orange: 'text-orange-600'
    };
    return colors[color] || colors.blue;
  };

  return (
    <UGCLayout className="pb-20">
      <NavigationBar items={ugcNavItems} hideMore hideLocks homePath="/app/ugc-dashboard" />
      <main className="max-w-7xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/20 border border-purple-500/40 mb-4">
            <Icon name="Users" size={16} color="#C084FC" />
            <span className="text-xs font-semibold text-purple-100">Community</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-3">Grow Together</h1>
          <p className="text-amber-100/80 max-w-3xl text-lg">
            Get feedback, collaborate with peers, and learn from successful creators. Your community for authentic growth.
          </p>
        </div>

        {!canAccess && (
          <div className="rounded-2xl border border-white/10 bg-slate-800/40 p-8 shadow-lg">
            <Icon name="Lock" size={32} className="text-purple-300 mb-3" />
            <h3 className="text-xl font-semibold text-white mb-2">Join the Community</h3>
            <p className="text-sm text-amber-100/80 mb-4">Unlock feedback tools, collaboration features, and peer support.</p>
            <Button onClick={() => (window.location.href = '/ugc-subscription')} className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg">
              View UGC Plans
            </Button>
          </div>
        )}

        {canAccess && (
          <div className="space-y-8">
            {/* Community Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="rounded-xl border border-border bg-gradient-to-br from-purple-50 to-white p-4 shadow-sm">
                <Icon name="Users" size={20} className="text-purple-600 mb-2" />
                <p className="text-2xl font-bold text-foreground">3.2k</p>
                <p className="text-xs text-muted-foreground">Active Creators</p>
              </div>
              <div className="rounded-xl border border-border bg-gradient-to-br from-blue-50 to-white p-4 shadow-sm">
                <Icon name="MessageSquare" size={20} className="text-blue-600 mb-2" />
                <p className="text-2xl font-bold text-foreground">450+</p>
                <p className="text-xs text-muted-foreground">Feedback Threads</p>
              </div>
              <div className="rounded-xl border border-border bg-gradient-to-br from-green-50 to-white p-4 shadow-sm">
                <Icon name="Award" size={20} className="text-green-600 mb-2" />
                <p className="text-2xl font-bold text-foreground">120</p>
                <p className="text-xs text-muted-foreground">Success Stories</p>
              </div>
              <div className="rounded-xl border border-border bg-gradient-to-br from-orange-50 to-white p-4 shadow-sm">
                <Icon name="TrendingUp" size={20} className="text-orange-600 mb-2" />
                <p className="text-2xl font-bold text-foreground">85%</p>
                <p className="text-xs text-muted-foreground">Response Rate</p>
              </div>
            </div>

            {/* Tabs */}
              <div className="flex gap-2 border-b border-white/10">
              {[
                { id: 'feedback', label: 'Feedback Hub', icon: 'MessageCircle' },
                { id: 'collaborate', label: 'Collaborate', icon: 'Users' },
                { id: 'resources', label: 'Resources', icon: 'BookOpen' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-all ${
                    activeTab === tab.id
                      ? 'border-purple-400 text-white font-semibold'
                      : 'border-transparent text-amber-200/70 hover:text-white'
                  }`}
                >
                  <Icon name={tab.icon} size={16} />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Feedback Tab */}
            {activeTab === 'feedback' && (
              <div className="space-y-6">
                <div className="rounded-xl border border-white/10 bg-white/5 p-6 shadow-sm">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Icon name="MessageSquare" size={20} className="text-purple-300" />
                    Request Feedback
                  </h2>
                  <div className="space-y-4">
                    <textarea
                      className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/50 resize-none"
                      placeholder="Share your video link, script, or hook for feedback... (paste TikTok/IG/YouTube link or describe your content)"
                      rows={3}
                    />
                    <div className="flex items-center justify-between">
                      <select className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white">
                        <option value="">Select feedback type</option>
                        <option value="hook">Hook Analysis</option>
                        <option value="cta">CTA Optimization</option>
                        <option value="script">Script Review</option>
                        <option value="engagement">Engagement Tips</option>
                        <option value="visual">Visual Quality</option>
                      </select>
                      <Button className="bg-purple-500 hover:bg-purple-600 text-white">
                        <Icon name="Send" size={14} className="mr-2" />
                        Post for Feedback
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Feedback Prompts Grid */}
                <section>
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Icon name="HelpCircle" size={20} className="text-blue-300" />
                    Feedback Prompts
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {feedbackPrompts.map((prompt, idx) => (
                      <div key={idx} className={`rounded-xl border p-5 shadow-sm ${getBgColorClass(prompt.color)}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <Icon name={prompt.icon} size={20} className={getIconColorClass(prompt.color)} />
                          <h3 className="text-md font-semibold text-white">{prompt.category}</h3>
                        </div>
                        <ul className="space-y-2">
                          {prompt.prompts.map((p, i) => (
                            <li key={i} className="text-xs text-amber-100/80 flex items-start gap-2">
                              <span className="text-green-600 mt-0.5">•</span>
                              {p}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Recent Feedback */}
                <section>
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Icon name="Activity" size={20} className="text-green-300" />
                    Recent Community Feedback
                  </h2>
                  <div className="space-y-3">
                    {sampleFeedback.map((item, idx) => (
                      <div key={idx} className="rounded-xl border border-border bg-white p-5 shadow-sm hover:shadow-md transition-all">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-semibold">
                              {item.creator.charAt(0)}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-foreground">{item.creator}</p>
                              <p className="text-xs text-muted-foreground">{item.timeAgo}</p>
                            </div>
                          </div>
                          <span className="px-2 py-1 rounded-md bg-purple-100 text-purple-700 text-xs font-semibold">
                            {item.category}
                          </span>
                        </div>
                        <p className="text-sm text-foreground mb-2 font-medium">{item.content}</p>
                        <div className="bg-white/5 rounded-lg p-3 mb-3 border border-white/10">
                          <p className="text-xs text-amber-100/80 italic">{item.preview}</p>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Icon name="MessageCircle" size={12} />
                            <span>{item.responses} responses</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Icon name="ThumbsUp" size={12} />
                            <span>{item.likes} helpful</span>
                          </div>
                          <Button size="xs" variant="ghost" className="ml-auto text-purple-300">
                            View Thread
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {/* Collaborate Tab */}
            {activeTab === 'collaborate' && (
              <div className="space-y-6">
                <div className="rounded-xl border border-white/10 bg-white/5 p-6 shadow-sm">
                  <h2 className="text-xl font-bold text-white mb-2">Find Your Creator Squad</h2>
                  <p className="text-sm text-amber-100/80 mb-4">
                    Collaboration amplifies reach and opens doors to bigger opportunities. Find your people here.
                  </p>
                  <div className="flex gap-3">
                    <Button className="bg-purple-500 hover:bg-purple-600 text-white">
                      <Icon name="Search" size={14} className="mr-2" />
                      Browse Creators
                    </Button>
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                      <Icon name="Plus" size={14} className="mr-2" />
                      Post Opportunity
                    </Button>
                  </div>
                </div>

                <section>
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Icon name="Zap" size={20} className="text-orange-300" />
                    Collaboration Types
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {collaborationOpportunities.map((collab, idx) => (
                      <div key={idx} className={`rounded-xl border p-6 shadow-sm hover:shadow-lg transition-all ${getBgColorClass(collab.color)}`}>
                        <Icon name={collab.icon} size={28} className={`${getIconColorClass(collab.color)} mb-3`} />
                        <h3 className="text-lg font-bold text-foreground mb-2">{collab.type}</h3>
                        <p className="text-sm text-muted-foreground mb-3">{collab.description}</p>
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center gap-2 text-xs text-foreground">
                            <Icon name="Users" size={12} />
                            <span>{collab.participants}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-green-600">
                            <Icon name="TrendingUp" size={12} />
                            <span>{collab.benefit}</span>
                          </div>
                        </div>
                        <Button size="sm" variant="outline" className="w-full">
                          Find Partners
                        </Button>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="rounded-xl border border-white/10 bg-white/5 p-6 shadow-sm">
                  <h2 className="text-lg font-bold text-white mb-4">How Collaboration Works</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-3">
                        <Icon name="Search" size={20} className="text-purple-300" />
                      </div>
                      <h3 className="text-sm font-semibold text-white mb-1">1. Find Matches</h3>
                      <p className="text-xs text-amber-100/80">Browse by niche, followers, or collab type</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-3">
                        <Icon name="MessageCircle" size={20} className="text-blue-300" />
                      </div>
                      <h3 className="text-sm font-semibold text-white mb-1">2. Connect</h3>
                      <p className="text-xs text-amber-100/80">DM to discuss goals and expectations</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-3">
                        <Icon name="CheckCircle" size={20} className="text-green-300" />
                      </div>
                      <h3 className="text-sm font-semibold text-white mb-1">3. Create & Grow</h3>
                      <p className="text-xs text-amber-100/80">Execute together and track results</p>
                    </div>
                  </div>
                </section>
              </div>
            )}

            {/* Resources Tab */}
            {activeTab === 'resources' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {communityResources.map((resource, idx) => (
                    <div key={idx} className="rounded-xl border border-border bg-white p-6 shadow-sm hover:shadow-lg transition-all">
                      <Icon name={resource.icon} size={32} className="text-purple-600 mb-4" />
                      <h3 className="text-lg font-bold text-foreground mb-2">{resource.title}</h3>
                      <p className="text-sm text-muted-foreground mb-4">{resource.description}</p>
                      <Button 
                        variant={resource.coming ? 'ghost' : 'default'}
                        className={resource.coming ? 'text-muted-foreground' : 'bg-purple-500 hover:bg-purple-600 text-white'}
                        disabled={resource.coming}
                      >
                        {resource.action}
                      </Button>
                    </div>
                  ))}
                </div>

                <section className="rounded-xl border border-border bg-gradient-to-br from-blue-50 to-white p-6 shadow-sm">
                  <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="BookOpen" size={20} className="text-blue-600" />
                    Quick Links
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Button variant="outline" className="justify-start" onClick={() => window.location.href = '/app/ugc-scripts'}>
                      <Icon name="FileText" size={16} className="mr-2" />
                      Script Templates
                    </Button>
                    <Button variant="outline" className="justify-start" onClick={() => window.location.href = '/app/teleprompter'}>
                      <Icon name="Video" size={16} className="mr-2" />
                      Teleprompter Tool
                    </Button>
                    <Button variant="outline" className="justify-start" onClick={() => window.location.href = '/app/ugc-brands'}>
                      <Icon name="Database" size={16} className="mr-2" />
                      Brand Database
                    </Button>
                  </div>
                </section>
              </div>
            )}
          </div>
        )}
      </main>
      <NovaChat />
    </UGCLayout>
  );
};

const UGCCommunityPageWithProvider = () => (
  <NovaChatProvider>
    <UGCCommunityPage />
  </NovaChatProvider>
);

export default UGCCommunityPageWithProvider;
